import { z } from 'zod'

export const APPLICATION_MENU_LABEL_MAX_LENGTH = 256
export const APPLICATION_MENU_MAX_SECTIONS = 20
export const APPLICATION_MENU_MAX_ITEMS_PER_SECTION = 50

/**
 * Nested Padlet transform URLs often exceed the 256-character label bound.
 * The schema caps the avatar URL so an unbounded string cannot enter the protocol.
 */
export const APPLICATION_MENU_AVATAR_URL_MAX_LENGTH = 2048

const ApplicationMenuLabelSchema = z.string().max(APPLICATION_MENU_LABEL_MAX_LENGTH)

export const ApplicationMenuItemSchema = z
  .object({
    actionId: z.string(),
    label: ApplicationMenuLabelSchema,
    sublabel: ApplicationMenuLabelSchema.optional(),
    enabled: z.boolean().optional(),
    checked: z.boolean().optional(),
    destructive: z.boolean().optional(),
    role: z.string().optional(),
    accelerator: z.string().optional(),
    /**
     * The web app sets this flag when the action changes the publishing tab. The shell must activate that tab first.
     */
    revealsSource: z.boolean().optional(),
  })
  .strict()

const ApplicationMenuSectionSchema = z
  .object({
    id: z.string(),
    items: z.array(ApplicationMenuItemSchema).max(APPLICATION_MENU_MAX_ITEMS_PER_SECTION),
  })
  .strict()

const ApplicationMenuIdentitySchema = z
  .object({
    label: ApplicationMenuLabelSchema,
    sublabel: ApplicationMenuLabelSchema,
    /**
     * avatarUrl is the only URL the protocol admits. The main process fetches it and the renderer never receives the string.
     */
    avatarUrl: z.string().max(APPLICATION_MENU_AVATAR_URL_MAX_LENGTH).optional(),
  })
  .strict()

export const ApplicationMenuModelSchema = z
  .object({
    revision: z.number().int().nonnegative(),
    identity: ApplicationMenuIdentitySchema,
    sections: z.array(ApplicationMenuSectionSchema).max(APPLICATION_MENU_MAX_SECTIONS),
  })
  .strict()

export const ApplicationMenuActionResultSchema = z.enum(['executed', 'cancelled', 'stale', 'unavailable', 'failed'])

export type ApplicationMenuItem = z.infer<typeof ApplicationMenuItemSchema>
export type ApplicationMenuSection = z.infer<typeof ApplicationMenuSectionSchema>
export type ApplicationMenuIdentity = z.infer<typeof ApplicationMenuIdentitySchema>
export type ApplicationMenuModel = z.infer<typeof ApplicationMenuModelSchema>
export type ApplicationMenuActionResult = z.infer<typeof ApplicationMenuActionResultSchema>

/**
 * Both sides share this action id for Settings. Do not treat the token as a page-local dashboard detail.
 */
export const APPLICATION_MENU_SETTINGS_ACTION_ID = 'settings'

/**
 * Both sides share this section id. The shell draws it in the Help menu rather than the app menu,
 * which macOS requires, so the token is contract rather than a page-local grouping.
 */
export const APPLICATION_MENU_HELP_SECTION_ID = 'helpCommands'
